import React, { useState, useEffect, useRef } from "react";
import { Play, Volume2, VolumeX, Users, Send, AlertCircle, ArrowRight, BookOpen, Clock, Heart, Award, ShieldCheck, Sparkles, MessageSquare, Menu } from "lucide-react";
import { CONFIG } from "../config";
import SEO from "../components/SEO";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function WebinarAccessPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [liveViewers, setLiveViewers] = useState(1284);
  const [activeTab, setActiveFaqTab] = useState("chat"); // 'chat' or 'qa' or 'handouts'
  const [chatMessages, setChatMessages] = useState([
    { id: 1, name: "Elena Rostova", message: "Hi everyone! Joining from Boston, super excited to learn how to structure my consulting offer.", isHost: false },
    { id: 2, name: "Marcus Brody", message: "Hello! AI niche matrix sounds like exactly what I need. I'm a technology trainer.", isHost: false },
    { id: 3, name: "Meenu Soni (Host)", message: "Welcome experts! We are going live shortly. Turn on your audio so you don't miss Secret 1.", isHost: true },
    { id: 4, name: "Dinesh Kumar", message: "Greetings from New Delhi. Ready with notebook!", isHost: false }
  ]);
  const [userMsg, setUserMsg] = useState("");
  const [offerTriggered, setOfferTriggered] = useState(false);
  const chatEndRef = useRef(null);

  // Simulated live audience ticker fluctuation
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveViewers((prev) => prev + (Math.random() > 0.5 ? 1 : -1) * Math.floor(Math.random() * 4 + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Simulated chat messages incoming
  useEffect(() => {
    const mockupIncoming = [
      "Wow! The breakdown of Secret 1 makes so much sense. I spent weeks overthinking my niche.",
      "Can we use Claude Sonnet for the niche matrices or is OpenAI better?",
      "Is the video money funnel hard to setup?",
      "Autopilot sales pipeline is the holy grail. So tired of manual client hunting.",
      "Just joined the WhatsApp community, the resources are fantastic!",
      "Meenu, does this work for executive business trainers?",
      "Your knowledge is already an asset... that really hit home.",
      "The simplicity of this model is refreshing compared to other complicated AI funnels.",
      "Are you showing templates for the digital product curriculum?",
      "This is gold! Bookmarking this now."
    ];

    const names = [
      "Sarah Jenkins", "Dr. Alex Vance", "Robert Carter", "Sophia Wu", "Aris Thorne",
      "Carlos Ruiz", "Yuki Tanaka", "Emily Stone", "Amir Al-Harbi", "Jessica Lind"
    ];

    const interval = setInterval(() => {
      const randomMsg = mockupIncoming[Math.floor(Math.random() * mockupIncoming.length)];
      const randomName = names[Math.floor(Math.random() * names.length)];
      
      setChatMessages((prev) => [
        ...prev,
        { id: Date.now(), name: randomName, message: randomMsg, isHost: false }
      ]);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Timely trigger of offer drop after 10 seconds for Wow experience
  useEffect(() => {
    const timer = setTimeout(() => {
      setOfferTriggered(true);
      toast.success("🔥 Special Offer Unlocked: AI Clarity Launchpad Package!");
    }, 12000);
    return () => clearTimeout(timer);
  }, []);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!userMsg.trim()) return;

    setChatMessages((prev) => [
      ...prev,
      { id: Date.now(), name: "You (Expert)", message: userMsg, isHost: false, isUser: true }
    ]);
    setUserMsg("");
    toast.success("Message sent to live feed");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans pb-16">
      <SEO 
        title="Live Broadcast Room | AI Clarity Coach Launchpad"
        description="Attend the exclusive free masterclass tutorial of the Knowledge-to-Income System."
      />

      {/* Top Header */}
      <header className="py-4 px-4 sm:px-6 border-b border-slate-800/80 bg-slate-900 sticky top-0 z-30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="font-extrabold text-white tracking-wider uppercase hover:text-teal-400 transition-colors">
            {CONFIG.creatorName}
          </Link>
          <span className="hidden sm:inline text-slate-600">|</span>
          <span className="hidden sm:inline text-xs text-slate-400 font-bold uppercase tracking-wider">{CONFIG.webinarTitle} Room</span>
        </div>

        <div className="flex items-center gap-3">
          {/* Status Badge */}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-bold border border-red-500/20">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-ping"></span>
            LIVE BROADCAST
          </span>

          {/* Audience Ticker */}
          <span className="hidden sm:flex items-center gap-1 px-3 py-1 rounded-full bg-slate-800 text-xs text-slate-300 font-semibold border border-slate-700/50">
            <Users className="w-3.5 h-3.5 text-sky-400" />
            <strong className="text-white font-mono">{liveViewers}</strong> live viewers
          </span>
        </div>
      </header>

      {/* Main Broadcast Layout */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* Left Column: Video Broadcast Player */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Custom Video Player Container */}
            <div 
              className="relative w-full aspect-video bg-slate-900 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden group"
              data-testid="webinar-live-stream-player"
            >
              {/* Image backdrop representing broadcast stage */}
              <img 
                src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1280&fit=crop"
                alt="Broadcast stream"
                className="absolute inset-0 w-full h-full object-cover opacity-80 select-none pointer-events-none"
              />

              {/* Dynamic Video Streaming Simulated Overlay */}
              {!isPlaying ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/70 p-6 text-center backdrop-blur-[2px] transition-all">
                  <button 
                    onClick={() => {
                      setIsPlaying(true);
                      setIsMuted(false);
                    }}
                    className="w-20 h-20 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all mb-4 cursor-pointer"
                    aria-label="Start streaming broadcast"
                  >
                    <Play className="w-10 h-10 fill-slate-950 translate-x-0.5" />
                  </button>
                  <p className="text-lg sm:text-xl font-black text-white tracking-tight">
                    {CONFIG.webinarTitle}
                  </p>
                  <p className="text-xs sm:text-sm text-teal-400 font-semibold mt-1">
                    Click Play to Enter Room & Join Meenu Soni Live
                  </p>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col justify-between p-4 bg-slate-950/10 pointer-events-none">
                  {/* Streaming indicator top bar */}
                  <div className="flex justify-between items-center w-full">
                    <span className="bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-md text-[10px] font-bold text-white flex items-center gap-1.5 border border-slate-800">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      CONNECTION: EXCELLENT (HD 1080P)
                    </span>
                    <button 
                      onClick={() => setIsMuted(!isMuted)}
                      className="pointer-events-auto p-2 bg-slate-950/80 hover:bg-slate-950 backdrop-blur-md rounded-lg text-white border border-slate-800 transition-colors"
                      aria-label={isMuted ? "Unmute stream" : "Mute stream"}
                    >
                      {isMuted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
                    </button>
                  </div>

                  {/* Simulated streaming timeline/overlay bottom */}
                  <div className="w-full bg-slate-950/70 backdrop-blur-md p-3 rounded-xl border border-slate-800 flex items-center justify-between gap-4 pointer-events-auto">
                    <span className="text-[10px] sm:text-xs text-slate-300 font-semibold flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                      Masterclass Broadcast Session Active
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] sm:text-xs text-slate-400 font-mono">00:12:45 / 01:15:00</span>
                      <div className="w-16 sm:w-24 h-1 bg-slate-800 rounded overflow-hidden">
                        <div className="w-[18%] h-full bg-teal-500"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Title / Description */}
            <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 rounded-3xl space-y-4">
              <div>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded bg-teal-500/10 text-teal-400 text-xs font-bold uppercase tracking-wider mb-2 border border-teal-500/20">
                  Current Session
                </span>
                <h1 className="text-xl sm:text-2xl font-black text-white">{CONFIG.webinarTitle}</h1>
                <p className="text-xs sm:text-sm font-semibold text-slate-400 mt-1">{CONFIG.webinarSubtitle}</p>
              </div>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed pt-2 border-t border-slate-800">
                {CONFIG.webinarPromise} AI Clarity Coach Launchpad maps the system of taking your hard-earned knowledge and setting it up as a beautiful, scalable personal brand offer with predictable autopilot client capture.
              </p>

              {/* Course bullet highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs sm:text-sm text-slate-300 font-semibold">
                {[
                  "Find your niche with AI assistants",
                  "Turn knowledge into a beautiful curriculum",
                  "Build a simple high-ticket Video Money Funnel",
                  "Understand autopilot sales machines"
                ].map((highlight, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-teal-400">✓</span>
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Chat Feed, QA, & handouts */}
          <div className="lg:col-span-4 h-full space-y-6">
            
            {/* Live Chat Panel */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl flex flex-col h-[520px]">
              
              {/* Tab Navigation header */}
              <div className="flex border-b border-slate-800/80 bg-slate-950 text-xs font-bold uppercase tracking-wider text-slate-400">
                <button 
                  onClick={() => setActiveFaqTab("chat")}
                  className={`flex-1 py-4 text-center border-b-2 transition-all ${activeTab === "chat" ? "border-teal-500 text-white bg-slate-900" : "border-transparent hover:text-slate-200"}`}
                >
                  Live Chat
                </button>
                <button 
                  onClick={() => setActiveFaqTab("qa")}
                  className={`flex-1 py-4 text-center border-b-2 transition-all ${activeTab === "qa" ? "border-teal-500 text-white bg-slate-900" : "border-transparent hover:text-slate-200"}`}
                  data-testid="webinar-qa-tab"
                >
                  Q&A
                </button>
                <button 
                  onClick={() => setActiveFaqTab("handouts")}
                  className={`flex-1 py-4 text-center border-b-2 transition-all ${activeTab === "handouts" ? "border-teal-500 text-white bg-slate-900" : "border-transparent hover:text-slate-200"}`}
                >
                  Resources
                </button>
              </div>

              {/* Tab 1: Live Chat Content */}
              {activeTab === "chat" && (
                <>
                  <div 
                    className="flex-1 p-4 overflow-y-auto space-y-4 font-sans text-sm select-none"
                    data-testid="webinar-chat-stream-container"
                  >
                    {chatMessages.map((msg) => (
                      <div key={msg.id} className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`font-extrabold ${msg.isHost ? "text-teal-400" : msg.isUser ? "text-sky-400" : "text-slate-300"}`}>
                            {msg.name}
                          </span>
                          {msg.isHost && (
                            <span className="text-[9px] bg-teal-500/20 text-teal-300 border border-teal-500/30 px-1 py-0.2 rounded font-black uppercase">Host</span>
                          )}
                        </div>
                        <p className="text-slate-400 leading-relaxed break-words">{msg.message}</p>
                      </div>
                    ))}
                    <div ref={chatEndRef}></div>
                  </div>

                  {/* Input sending footer */}
                  <form onSubmit={handleSendMessage} className="p-3 bg-slate-950 border-t border-slate-800 flex gap-2">
                    <input 
                      type="text"
                      placeholder="Ask Meenu a question or comment..."
                      value={userMsg}
                      onChange={(e) => setUserMsg(e.target.value)}
                      className="flex-1 bg-slate-900 border border-slate-800 focus:outline-none focus:ring-1 focus:ring-teal-500 rounded-xl px-4 py-2 text-xs text-white"
                      data-testid="webinar-chat-input-field"
                    />
                    <button 
                      type="submit"
                      className="p-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-xl transition-all cursor-pointer"
                      data-testid="webinar-chat-send-button"
                      aria-label="Send message"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  </form>
                </>
              )}

              {/* Tab 2: Q&A Content */}
              {activeTab === "qa" && (
                <div className="flex-1 p-6 space-y-4 text-xs sm:text-sm text-slate-400 overflow-y-auto">
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                    <p className="font-extrabold text-white">How long do we get access to the replay?</p>
                    <p className="mt-1 text-slate-400 leading-relaxed">The private replay is guaranteed for exactly 48 hours following the live masterclass event.</p>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl">
                    <p className="font-extrabold text-white">Is there a templates package included?</p>
                    <p className="mt-1 text-slate-400 leading-relaxed">Yes! Meenu will share a private PDF outline of the AI Niche Matrix prompt template inside the WhatsApp VIP Community.</p>
                  </div>
                  <div className="text-center py-6 text-slate-500 text-xs">
                    More questions? Type them directly in the chat or post inside WhatsApp community.
                  </div>
                </div>
              )}

              {/* Tab 3: Handouts / Resources Content */}
              {activeTab === "handouts" && (
                <div className="flex-1 p-6 space-y-4 text-xs sm:text-sm overflow-y-auto">
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between gap-3">
                    <div>
                      <p className="font-extrabold text-white">Webinar workbook PDF</p>
                      <p className="text-xs text-slate-500 mt-0.5">Prepare with our structured notes</p>
                    </div>
                    <button 
                      onClick={() => toast.success("Workbook PDF download initiated!")}
                      className="px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded font-bold text-xs"
                    >
                      Download
                    </button>
                  </div>
                  <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between gap-3">
                    <div>
                      <p className="font-extrabold text-white">AI Niche Matrix Template</p>
                      <p className="text-xs text-slate-500 mt-0.5">The complete prompt library</p>
                    </div>
                    <button 
                      onClick={() => toast.success("Prompt Library download initiated!")}
                      className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded font-bold text-xs"
                    >
                      Download
                    </button>
                  </div>
                </div>
              )}

            </div>

          </div>

        </div>

        {/* TIMELY TRIGGERED SPECIAL OFFER DROP */}
        {offerTriggered && (
          <div className="mt-12 bg-gradient-to-r from-teal-950 to-slate-950 rounded-3xl border border-teal-500/30 p-6 sm:p-10 relative overflow-hidden shadow-2xl animate-fade-in">
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl"></div>
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
              
              <div className="text-center md:text-left space-y-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded bg-teal-500/20 text-teal-300 text-xs font-black uppercase tracking-widest border border-teal-500/30">
                  <Sparkles className="w-3.5 h-3.5" /> Webinar Exclusive Offer
                </span>
                <h3 className="text-xl sm:text-2xl font-black text-white">
                  Join the AI Clarity Launchpad System
                </h3>
                <p className="text-sm text-slate-400 max-w-xl leading-relaxed">
                  Ready to turn your knowledge into a clear coaching offer, digital product, and automated video funnel with 1-on-1 support? Unlock the full system package today.
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col sm:flex-row items-center gap-3">
                <a 
                  href={CONFIG.launchpadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-teal-500 hover:bg-teal-400 text-slate-950 font-black rounded-xl transition-all shadow-lg text-sm sm:text-base text-center block cursor-pointer"
                  data-testid="webinar-offer-cta-button"
                >
                  EXPLORE THE LAUNCHPAD OFFER
                </a>
                <button 
                  onClick={() => setOfferTriggered(false)}
                  className="text-xs text-slate-500 hover:text-slate-400 font-bold underline px-4 py-2"
                >
                  Dismiss Offer
                </button>
              </div>

            </div>
          </div>
        )}

      </main>
    </div>
  );
}
